/**
 * Conversion between an ISO instant and the value a `datetime-local` input
 * holds.
 *
 * Dates that the server ENFORCES are edited as instants, not as bare days: a
 * date-only control silently means midnight, so "closes 10 August" would shut
 * the window at the START of the 10th — a full day earlier than the person
 * setting it intends. The input works in local wall-clock time (what the user
 * sees on their own clock) and the wire always carries UTC.
 */

const pad = (n: number): string => String(n).padStart(2, '0');

/** ISO instant → the local wall-clock value a `datetime-local` input expects. */
export function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Local wall-clock input → ISO instant. Blank stays undefined so a caller can
 * omit an optional key entirely rather than sending a fabricated "now".
 */
export function toIsoInstant(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/**
 * "3 days ago", "in 6 days" — a distance in the operator's own language.
 *
 * `Intl.RelativeTimeFormat` rather than a hand-rolled table, because plural and
 * gender rules for Kinyarwanda are not something to reimplement, and the browser
 * already has them. Falls back to the absolute date on any locale the runtime
 * cannot format, which is a worse answer than a relative one but never a wrong
 * one.
 */
export function relativeTime(iso: string | null | undefined, locale: string, now: number = Date.now()): string | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return null;

  const seconds = Math.round((at - now) / 1000);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.35],
    ['month', 12],
    ['year', Number.POSITIVE_INFINITY],
  ];

  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    let value = seconds;
    for (const [unit, size] of units) {
      if (Math.abs(value) < size) return rtf.format(Math.round(value), unit);
      value /= size;
    }
    return rtf.format(Math.round(value), 'year');
  } catch {
    return new Date(at).toLocaleDateString(locale);
  }
}

/**
 * How stale the numbers on screen are.
 *
 * Judges score while this screen is open, so a decision taken against a
 * forty-minute-old count is a decision taken against the wrong count. The age is
 * shown rather than implied — "as of 12:04" is checkable, a silently refreshing
 * number is not.
 */
export function asOf(updatedAt: number | null, locale: string): string | null {
  if (updatedAt === null) return null;
  return new Date(updatedAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}
